import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isVoiceSupported, startRecording, blobExtension } from "@/lib/voice";
import { toast } from "sonner";
import api from "@/lib/api";

export default function VoiceRecorder({ open, onClose, onResult, language }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const recRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setState("idle");
      setSeconds(0);
      setTranscript("");
    }
  }, [open]);

  const begin = async () => {
    if (!isVoiceSupported()) {
      toast.error("Voice recording not supported in this browser");
      return;
    }
    try {
      recRef.current = await startRecording();
      setState("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const finish = async () => {
    if (!recRef.current) return;
    if (tickRef.current) clearInterval(tickRef.current);
    setState("uploading");
    try {
      const blob = await recRef.current.stop();
      const mime = recRef.current.mime;
      recRef.current = null;
      const ext = blobExtension(mime);
      const fd = new FormData();
      fd.append("file", new File([blob], `voice.${ext}`, { type: mime }));
      if (language) fd.append("language", language);
      const { data } = await api.post("/voice/stt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(data.text || "");
      setState("idle");
    } catch (e) {
      toast.error("Transcription failed");
      setState("idle");
    }
  };

  const insert = () => {
    onResult?.(transcript);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="voice-recorder-dialog">
        <DialogHeader>
          <DialogTitle>Voice typing</DialogTitle>
          <DialogDescription>
            Speak in any supported language. TeeBee Voice will transcribe and insert the text for you.
          </DialogDescription>
        </DialogHeader>
        <div className="py-3 flex flex-col items-center gap-4">
          <div className={`relative h-24 w-24 rounded-full grid place-items-center ${state === "recording" ? "bg-rose-500/15" : "bg-violet-500/10"}`}>
            {state === "recording" && (
              <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-ping" />
            )}
            <Mic className={`relative h-10 w-10 ${state === "recording" ? "text-rose-600" : "text-violet-600"}`} />
          </div>
          {state === "recording" && (
            <p className="font-display text-2xl tracking-tight">{formatTime(seconds)}</p>
          )}
          {state === "uploading" && (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Transcribing…
            </p>
          )}
          {transcript && state === "idle" && (
            <div className="w-full rounded-2xl border border-border p-4 bg-muted/40">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Transcript</p>
              <p className="mt-2 text-sm whitespace-pre-wrap">{transcript}</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {state === "idle" && !transcript && (
            <Button onClick={begin} className="rounded-full" data-testid="voice-start">
              <Mic className="h-4 w-4 mr-1.5" /> Start recording
            </Button>
          )}
          {state === "recording" && (
            <Button onClick={finish} variant="destructive" className="rounded-full" data-testid="voice-stop">
              <Square className="h-4 w-4 mr-1.5" /> Stop
            </Button>
          )}
          {state === "idle" && transcript && (
            <>
              <Button variant="outline" onClick={() => setTranscript("")} className="rounded-full">
                Re-record
              </Button>
              <Button onClick={insert} className="rounded-full" data-testid="voice-insert">
                <Check className="h-4 w-4 mr-1.5" /> Insert
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}
